import mixpanel from 'mixpanel-browser';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { identify, init } from '@chamaeleonidae/chmln';
import { datadogRum } from '@datadog/browser-rum';

import useEnvironment from './hooks/useEnvironment';
import {
    selectCurrentUser,
    selectFacility,
    selectFacilityMetadata,
    selectIsObservationsEnabled,
    selectIsOnCall,
    selectRecurringBlocks,
    selectStaffMemberFacilityConfigs,
    selectStaffMemberId,
    selectUserCareZones,
} from './store/configSlice';
import { CareZone, StaffMemberGroupMembership } from './store/sageAdminApi';
import isEmpty from 'lodash/isEmpty';
import { useSageAuth } from 'src/auth/useSageAuth';
import { useDeviceInfo } from 'src/hooks/useDeviceInfo';
import { IS_MIXPANEL_DISABLED } from 'src/mixpanel';
import { getAppPlatform, NativeShellInfo, readNativeShellInfo } from 'src/native/appInfo';

declare global {
    interface Window {
        enableLogEvents?: () => void;
        disableLogEvents?: () => void;
        mixpanel?: any;
        chmln?: any;
    }
}

export type EventBody = {
    [key: string]: any;
};

export type TrackEventOptions = {
    sendToDataDog?: boolean;
    sendToMixpanel?: boolean;
};

export const ObjectTypes = {
    Backdrop: 'Backdrop',
    Banner: 'Banner',
    Button: 'Button',
    Card: 'Card',
    Checkbox: 'Checkbox',
    Dialog: 'Dialog',
    Drawer: 'Drawer',
    Form: 'Form',
    Icon: 'Icon',
    Keypress: 'Keypress',
    Link: 'Link',
    Notification: 'Notification',
    Input: 'Input',
    Page: 'Page',
    Radio: 'Radio',
    Select: 'Select',
};

export type SearchInputAnalyticsHandlers = {
    onFocus: () => void;
    onBlur: () => void;
    onClear: () => void;
    onCancel: () => void;
};

interface AnalyticsContextType {
    baseEventBody: EventBody;
    trackEvent: (eventName: string, eventBody?: EventBody, options?: TrackEventOptions) => void;
    trackPageView: (pageName: string, additionalProperties?: EventBody) => void;
    trackDrawerView: (drawerName: string, additionalProperties?: EventBody) => void;
    trackSearchInput: (context: string, label: string) => SearchInputAnalyticsHandlers;
}

export const AnalyticsContext = createContext({
    baseEventBody: {} as EventBody,
    trackEvent: (
        _eventName: string,
        _eventBody: EventBody = {},
        _options: TrackEventOptions = {}
    ) => {},
    trackPageView: (_pageName: string, _eventBody: EventBody = {}) => {},
    trackDrawerView: (_drawerName: string, _eventBody: EventBody = {}) => {},
    trackSearchInput: (_context: string, _label: string) =>
        ({
            onFocus: () => {},
            onBlur: () => {},
            onClear: () => {},
            onCancel: () => {},
        } as SearchInputAnalyticsHandlers),
});

interface AnalyticsProviderProps {
    children: React.ReactNode;
}

function AnalyticsProvider(props: AnalyticsProviderProps) {
    const { user: auth0User } = useSageAuth();
    const { pathname } = useLocation();

    const mdmDeviceInfo = useDeviceInfo();

    const currentUserProfile = useSelector(selectCurrentUser);
    const isObservationsEnabled = useSelector(selectIsObservationsEnabled);
    const isOnCall = useSelector(selectIsOnCall);
    const facility = useSelector(selectFacility);
    const facilityMetadata = useSelector(selectFacilityMetadata);
    const recurringNotificationBlocks = useSelector(selectRecurringBlocks);
    const staffMemberFacilityConfigs = useSelector(selectStaffMemberFacilityConfigs);
    const staffMemberProfileId = useSelector(selectStaffMemberId);
    const userCareZones = useSelector(selectUserCareZones);

    const { isProd, isDev, isBouncer } = useEnvironment();

    const [nativeShellInfo, setNativeShellInfo] = useState<NativeShellInfo>();
    const [shouldLogEvents, setShouldLogEvents] = useState(false);
    const [shouldLogFullEventBody, setShouldLogFullEventBody] = useState(false);

    useEffect(() => {
        if (window) {
            window.enableLogEvents = (includeFullEventBody = true) => {
                console.info(
                    'Event logging enabled. Include full event body:',
                    includeFullEventBody
                );
                setShouldLogEvents(true);
                setShouldLogFullEventBody(includeFullEventBody);
            };
            window.disableLogEvents = () => {
                console.info('Event logging disabled');
                setShouldLogEvents(false);
                setShouldLogFullEventBody(false);
            };
        }

        return () => {
            if (window) {
                window.enableLogEvents = undefined;
                window.disableLogEvents = undefined;
            }
        };
    }, []);

    useEffect(() => {
        if (isProd || isDev || isBouncer) {
            /**
             * Initialize Chameleon
             *
             * Note: the custom data attribute "data-chameleon-id" is attached to
             * any element that is expected to be queried by a tour in Chameleon, usually as
             * a trigger criteria for displaying the tour to the user.
             */
            init(import.meta.env.VITE_CHAMELEON_ID, {
                fastUrl: import.meta.env.VITE_CHAMELEON_URL,
            });
        }
    }, [isBouncer, isDev, isProd]);

    useEffect(() => {
        readNativeShellInfo().then((info) => {
            if (info) {
                setNativeShellInfo(info);
            }
        });
    }, []);

    useEffect(() => {
        const { lastUpdatedTimestamp, ...deviceAttributes } = mdmDeviceInfo || {};
        if (!isEmpty(deviceAttributes)) {
            datadogRum.setGlobalContextProperty('device', deviceAttributes);
        } else {
            datadogRum.removeGlobalContextProperty('device');
        }
    }, [mdmDeviceInfo]);

    useEffect(() => {
        if (auth0User && currentUserProfile) {
            try {
                const { id: facilityId = '', name: facilityName = '', enterprise } = facility || {};

                datadogRum?.setUser({
                    id: auth0User?.sub,
                    name: `${auth0User?.given_name ?? ''} ${auth0User?.family_name ?? ''}`,
                    email: currentUserProfile?.email,
                    enterpriseId: enterprise?.id,
                    enterpriseName: enterprise?.name,
                    facilityId: facilityId,
                    facilityName: facilityName,
                    groups: facilityMetadata?.groups?.map(
                        ({ groupName }: StaffMemberGroupMembership) => groupName
                    ),
                });

                if (facilityId && facilityName) {
                    datadogRum?.setAccount({
                        id: facilityId,
                        name: facilityName,
                        facilityId: facilityId,
                        facilityName: facilityName,
                        enterpriseId: enterprise?.id,
                        enterpriseName: enterprise?.name,
                    });
                }
            } catch (e) {
                console.error('Unable to attach user to datadog:', e);
            }
        }
    }, [auth0User, currentUserProfile, facility, facilityMetadata]);

    const baseEventBody = useMemo(
        () => ({
            pathname,
            appVersion: import.meta.env.VITE_VERSION,
            platform: getAppPlatform(),
            shellBuild: nativeShellInfo?.build,
            shellVersion: nativeShellInfo?.version,
            enterpriseId: facility?.enterprise?.id,
            enterpriseName: facility?.enterprise?.name,
            facilityId: facility?.id,
            facilityName: facility?.name,
            facilityStatus: facility?.status,
            groups: facilityMetadata?.groups?.map(
                ({ groupName }: StaffMemberGroupMembership) => groupName
            ),
            profileId: staffMemberProfileId,
            isOnCall: !!isOnCall,
        }),
        [
            facility,
            facilityMetadata?.groups,
            isOnCall,
            nativeShellInfo,
            pathname,
            staffMemberProfileId,
        ]
    );

    useEffect(() => {
        // Identify user in Chameleon
        if (
            (isProd || isDev || isBouncer) &&
            staffMemberFacilityConfigs &&
            auth0User?.sub &&
            facilityMetadata
        ) {
            try {
                identify(auth0User?.sub, {
                    ...baseEventBody,
                    isAgencyStaff: staffMemberFacilityConfigs?.isAgencyStaff,
                    isCareAdmin: staffMemberFacilityConfigs?.isCareAdmin,
                    jobTitle: staffMemberFacilityConfigs?.jobTitle,
                    userCareZones: userCareZones?.map(({ name }: CareZone) => name),
                });
            } catch (error) {
                datadogRum.addError(error, { message: 'Error identifying user in Chameleon' });
            }
        }
    }, [
        auth0User,
        baseEventBody,
        facilityMetadata,
        isBouncer,
        isDev,
        isProd,
        staffMemberFacilityConfigs,
        userCareZones,
    ]);

    // Track user actions in Datadog
    // See https://docs.datadoghq.com/real_user_monitoring/guide/send-rum-custom-actions/?tab=npm#instrument-your-code
    const trackDatadogAction = (actionName: string, payload: EventBody) => {
        try {
            datadogRum?.addAction(actionName, payload);
        } catch (error) {
            console.error('Error tracking datadog action:', error);
        }
    };

    const trackMixpanelEvent = (eventName: string, payload: EventBody) => {
        try {
            mixpanel?.track?.(eventName, payload);
        } catch (error) {
            console.error('Error tracking mixpanel event:', error);
        }
    };

    const trackEvent = useCallback(
        (eventName: string, eventBody: EventBody = {}, options: TrackEventOptions = {}) => {
            const fullEventBody = { ...baseEventBody, ...eventBody };

            const { sendToMixpanel = true, sendToDataDog = true } = options;

            if (shouldLogEvents) {
                console.info('\nEvent:', eventName);
                const eventBodyToLog = shouldLogFullEventBody ? fullEventBody : eventBody;

                // Convert arrays into CSV strings for easier readability in console.table
                const formattedEventBody = Object.entries(eventBodyToLog).reduce(
                    (acc, [key, value]) => {
                        // eslint-disable-next-line no-param-reassign
                        acc[key] = Array.isArray(value) ? value.join(', ') : value;
                        return acc;
                    },
                    {} as Record<string, any>
                );

                console.table(formattedEventBody);
            }

            if (IS_MIXPANEL_DISABLED) {
                return;
            }

            if (!eventName) {
                console.warn('No event name provided');
            } else {
                sendToDataDog && trackDatadogAction(eventName, eventBody);
                sendToMixpanel && trackMixpanelEvent(eventName, fullEventBody);
            }
        },
        [baseEventBody, shouldLogEvents, shouldLogFullEventBody]
    );

    const trackPageView = useCallback(
        (pageName: string, additionalProperties: EventBody = {}) => {
            trackEvent('Page Viewed', {
                ...additionalProperties,
                pageName,
                objectType: ObjectTypes.Page,
            });
        },
        [trackEvent]
    );

    const trackDrawerView = useCallback(
        (drawerName: string, additionalProperties: EventBody = {}) => {
            trackEvent('Drawer Viewed', {
                ...additionalProperties,
                drawerName,
                objectType: ObjectTypes.Drawer,
            });
        },
        [trackEvent]
    );

    // Returns SearchInput focus/blur/clear/cancel handlers that track analytics.
    const trackSearchInput = useCallback(
        (context: string, label: string): SearchInputAnalyticsHandlers => ({
            onFocus: () => trackEvent('Search Input Focused', { context, label }),
            onBlur: () => trackEvent('Search Input Blurred', { context, label }),
            onClear: () => trackEvent('Search Input Cleared', { context, label }),
            onCancel: () => trackEvent('Search Input Cancelled', { context, label }),
        }),
        [trackEvent]
    );

    const contextValues = useMemo(
        () => ({
            baseEventBody,
            trackEvent,
            trackPageView,
            trackDrawerView,
            trackSearchInput,
        }),
        [baseEventBody, trackEvent, trackPageView, trackDrawerView, trackSearchInput]
    );

    return (
        <AnalyticsContext.Provider value={contextValues}>
            {/* Hidden placeholder divs used for Chameleon */}
            {recurringNotificationBlocks.length > 0 && (
                <div
                    data-chameleon-id="has-recurring-escalations"
                    style={{ visibility: 'hidden' }}
                />
            )}
            {isObservationsEnabled && (
                <div data-chameleon-id="observations-enabled" style={{ visibility: 'hidden' }} />
            )}
            {props.children}
        </AnalyticsContext.Provider>
    );
}

export function useAnalytics(): AnalyticsContextType {
    const context = useContext(AnalyticsContext);
    if (context === undefined) {
        throw new Error('useAnalytics must be used within an AnalyticsProvider');
    }
    return context;
}

export default AnalyticsProvider;
